import { urlDateFormat } from 'common/UrlDate';
import { DateTime } from 'luxon';
import React from 'react';
import { IDeliveryDetails } from 'src/checkout/IDeliveryDetails';
import { ConsumerOrderCartDeliveryBanner } from 'src/pages/consumer/order/components/ConsumerOrderCartDeliveryBanner/ConsumerOrderCartDeliveryBanner';
import { useSelector } from 'src/store';
import {
  selectAddOnItems,
  selectAvailableAddOnItems,
  selectCart,
  selectLineItemQuantitiesInEditingOrder,
  selectMultiShefCartSubtotal,
  selectUnavailableLineItemIds,
} from 'src/store/cart/selectors';
import { CartActionLocation, CartFoodItem, ServerAddOnItem, WholeCartLineItem } from 'src/store/cart/types';
import {
  selectAvailableCheckoutShefIds,
  selectCheckoutOrders,
  selectUnavailableCheckoutOrders,
} from 'src/store/checkout';
import { Spacing } from 'web-common/src/shared/styles';
import { ShefsUnavailableError } from './cart/ShefsUnavailableError';
import { useItemQuantifyModifiers, useVisibleAddOnItems } from './ConsumerOrderCartInfoHooks';
import { ConsumerOrderCartLineItems } from './ConsumerOrderCartLineItems';
import { ConsumerOrderCartSubtotals } from './ConsumerOrderCartSubtotals';
import { MultiCartAddOnItem } from './MultiCart/MultiCartAddOnItem';

interface IConsumerOrderCartInfoProps {
  publicName: string | null | undefined;
  deliveryDate: DateTime;
  pathToShefsMenu?: string; // This is basically used to determine whether we're mobile full screen cart
  setFoodItem?: (params: {
    foodItem: CartFoodItem;
    quantity: number;
    deliveryDate: string;
    location: CartActionLocation;
  }) => void;
  setAddOnQuantity?: (params: { addOnItem: ServerAddOnItem; quantity: number; shefId: string }) => void;
  showUnavailableItems?: boolean;
  showFullTotals: boolean;
  isEditingOrder: boolean;
  isPickupOrder?: boolean | null;
  deliveryDetails?: IDeliveryDetails;
  showDishImages?: boolean;
  fulfillmentOption?: string;
  timeWindowId?: number | null;
}

export type UpdatableItemType = (WholeCartLineItem | ServerAddOnItem) & { unavailable?: boolean };
export const isAddOnItem = (item: WholeCartLineItem | ServerAddOnItem): item is ServerAddOnItem =>
  Boolean((item as ServerAddOnItem)?.addonType);
export const hasNonNullImage = <T extends { imageUrl?: unknown }>(
  item: T | undefined
): item is T & { imageUrl: Exclude<T['imageUrl'], null | undefined> } =>
  item?.imageUrl !== null && item?.imageUrl !== undefined;
export const getUpdatableItemImageUrl = (item: UpdatableItemType) => {
  if (isAddOnItem(item)) return item.imageUrl;
  return item.foodItem.imageUrl;
};

export const ConsumerOrderCartInfo: React.FC<IConsumerOrderCartInfoProps> = ({
  deliveryDate,
  setFoodItem,
  setAddOnQuantity,
  showUnavailableItems = false,
  showDishImages = false,
  showFullTotals,
  isEditingOrder,
  isPickupOrder,
  deliveryDetails,
  fulfillmentOption,
  ...rest
}) => {
  const deliveryUrlDate = deliveryDate.toFormat(urlDateFormat);

  const {
    isMultiCart,
    availableAddOnItems,
    addOnItems,
    lineItemQuantitiesInEditingOrder,
    unavailableLineItemIds,
    subtotal,
    checkoutOrders,
    unavailableCheckoutOrders,
  } = useSelector((state) => {
    const cart = selectCart(state, isEditingOrder);
    const shefIds = selectAvailableCheckoutShefIds(state, cart);

    return {
      isMultiCart: cart.isMultiCart,
      addOnItems: selectAddOnItems(cart),
      availableAddOnItems: selectAvailableAddOnItems(cart),
      unavailableLineItemIds: selectUnavailableLineItemIds(cart),
      lineItemQuantitiesInEditingOrder: selectLineItemQuantitiesInEditingOrder(cart),
      checkoutOrders: selectCheckoutOrders(state, cart),
      unavailableCheckoutOrders: selectUnavailableCheckoutOrders(state, cart),
      subtotal: selectMultiShefCartSubtotal(cart, shefIds),
    };
  });

  const { increment, decrement } = useItemQuantifyModifiers({ isEditingOrder, deliveryDate: deliveryUrlDate });
  const visibleAddonItems = useVisibleAddOnItems({ availableAddOnItems, addOnItems, setAddOnQuantity });
  const unavailableShefs = unavailableCheckoutOrders.map((order) => order.shef.name);

  return (
    <div className='flex flex-col' {...rest}>
      <span className='px-2 py-3 text-lg font-bold text-neutral-800'>
        Your order for {isPickupOrder ? 'pick-up' : 'delivery'} on&nbsp;
        {deliveryDate && (
          <>
            {deliveryDate.toLocaleString({
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </>
        )}
      </span>

      <div className='flex flex-col gap-2'>
        {unavailableShefs.length > 0 && <ShefsUnavailableError shefNames={unavailableShefs} />}
        {checkoutOrders.map((order) => (
          <ConsumerOrderCartLineItems
            key={order.shef.id}
            isMultiCart={isMultiCart}
            capacityInfo={order.capacityInfo}
            decrement={decrement}
            deliveryDate={order.deliveryDate}
            increment={increment}
            isEditable={Boolean(setFoodItem)}
            lineItemQuantitiesInEditingOrder={lineItemQuantitiesInEditingOrder}
            lineItems={order.lineItems}
            shef={order.shef}
            showDishImages={showDishImages}
            unavailableLineItemIds={unavailableLineItemIds}
            showUnavailableItems={showUnavailableItems}
            isEditingOrder={isEditingOrder}
          />
        ))}

        <div className='flex flex-col overflow-hidden rounded-lg'>
          {visibleAddonItems.map(([addOnItem, quantity]) => (
            <MultiCartAddOnItem
              key={addOnItem.id}
              addOnItem={addOnItem}
              quantity={quantity}
              paddingX={Spacing.HALF}
              paddingY={Spacing.HALF}
            />
          ))}
        </div>
      </div>

      {!isPickupOrder && !showFullTotals && (
        <ConsumerOrderCartDeliveryBanner subtotal={subtotal} fulfillmentOption={fulfillmentOption} />
      )}

      {showFullTotals && (
        <ConsumerOrderCartSubtotals
          isEditingOrder={isEditingOrder}
          isFinal={true}
          deliveryDetails={deliveryDetails}
          isPickupOrder={isPickupOrder}
        />
      )}
    </div>
  );
};
